import { lazy, Suspense, useContext, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FlaskConical, Play } from 'lucide-react';
import { Dialog, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import OpenApiDocumentContext from '../OpenApiProvider/OpenApiDocumentContext';
import PlaygroundSkeleton from './PlaygroundSkeleton';
import { DialogHeading, PlaygroundDialog } from './styled';
import type { EndpointOperationProps } from '../types';

const EndpointPlayground = lazy(() => import('./index'));

const PlaygroundLauncher = ({ endpoint }: EndpointOperationProps) => {
    const [open, setOpen] = useState(false);
    const context = useContext(OpenApiDocumentContext);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const sendRequestRef = useRef<(() => void) | null>(null);
    const { agentId: organizationId = '' } = useParams();
    if (!context) return null;
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button
                    variant="default"
                    size="sm"
                    aria-label={`Test it: ${endpoint.method.toUpperCase()} ${endpoint.path}`}
                >
                    <Play size={14} aria-hidden="true" /> Test it
                </Button>
            </DialogTrigger>
            {open ? (
                <PlaygroundDialog
                    onOpenAutoFocus={event => {
                        event.preventDefault();
                        titleRef.current?.focus();
                    }}
                    onKeyDown={event => {
                        if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
                            event.preventDefault();
                            sendRequestRef.current?.();
                        }
                    }}
                >
                    <DialogHeading>
                        <FlaskConical aria-hidden="true" />
                        <div>
                            <DialogTitle ref={titleRef} tabIndex={-1}>
                                {endpoint.summary}
                            </DialogTitle>
                            <DialogDescription>
                                Test this endpoint with your signed-in account.
                            </DialogDescription>
                        </div>
                    </DialogHeading>
                    <Suspense fallback={<PlaygroundSkeleton />}>
                        <EndpointPlayground
                            key={`${endpoint.id}:${organizationId}`}
                            endpoint={endpoint}
                            document={context.document}
                            organizationId={organizationId}
                            sendRequestRef={sendRequestRef}
                        />
                    </Suspense>
                </PlaygroundDialog>
            ) : null}
        </Dialog>
    );
};

export default PlaygroundLauncher;
